/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type AddPayslipPayload,
  type CreateRunPayload,
  type PayrollRosterRow,
  type PayrollRun,
  type PayrollRunDetail,
  payrollApi,
} from '../api/payroll.js';

const RUNS_KEY = ['payroll', 'runs'] as const;
const runKey = (id: string): readonly unknown[] => ['payroll', 'runs', id];

export function usePayrollRoster(): UseQueryResult<readonly PayrollRosterRow[]> {
  return useQuery({ queryKey: ['payroll', 'roster'], queryFn: () => payrollApi.roster() });
}

export function usePayrollRuns(): UseQueryResult<readonly PayrollRun[]> {
  return useQuery({ queryKey: RUNS_KEY, queryFn: () => payrollApi.listRuns() });
}

export function usePayrollRun(id: string | undefined): UseQueryResult<PayrollRunDetail> {
  return useQuery({
    queryKey: runKey(id ?? ''),
    queryFn: () => payrollApi.getRun(id ?? ''),
    enabled: id !== undefined && id !== '',
  });
}

export function useCreatePayrollRun(): UseMutationResult<PayrollRun, Error, CreateRunPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRunPayload) => payrollApi.createRun(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RUNS_KEY });
    },
  });
}

interface AddPayslipVars {
  readonly runId: string;
  readonly payload: AddPayslipPayload;
}

export function useAddPayslip(): UseMutationResult<PayrollRun, Error, AddPayslipVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: AddPayslipVars) => payrollApi.addPayslip(vars.runId, vars.payload),
    onSuccess: (_run, vars) => {
      void qc.invalidateQueries({ queryKey: runKey(vars.runId) });
      void qc.invalidateQueries({ queryKey: RUNS_KEY });
    },
  });
}

export function useProcessPayrollRun(): UseMutationResult<PayrollRun, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => payrollApi.process(id),
    onSuccess: (_run, id) => {
      void qc.invalidateQueries({ queryKey: runKey(id) });
      void qc.invalidateQueries({ queryKey: RUNS_KEY });
    },
  });
}
